/**
 * SitesButton — opens the project's External Website(s) in a new tab.
 *
 * Shown wherever the running application is presented to a user (the Run
 * view's top bar). A single site renders a direct "Site" button; multiple
 * sites render a dropdown listing each one. The site URL mirrors the
 * backend's slug resolution (spec.slug || obj_name || slugify(title)).
 */
import { useEffect, useRef, useState } from 'react'
import { Globe, ChevronDown } from 'lucide-react'
import { siteAddress } from '@/config/hostBinding'

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export interface SiteGroup {
  name: string
  type?: string
  obj_name?: string
  functional_specs?: string
  objects?: Array<{ type: string; obj_name?: string; functional_specs?: string }>
}

export default function SitesButton({
  sites, userId, project, iconOnly = false,
}: {
  sites: SiteGroup[]
  userId: string
  project: string
  iconOnly?: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const siteUrl = (group: SiteGroup): string => {
    // Mirror the backend's slug resolution (site_from_nav.find_site_with_nav):
    //   spec.slug || obj_name || slugify(title)
    const selfObj = group.objects?.find((o) => o.type === 'X')
    const specJson = group.functional_specs || selfObj?.functional_specs || '{}'
    const objName = group.obj_name || selfObj?.obj_name || ''
    let slug = ''
    try { slug = JSON.parse(specJson).slug || '' } catch { /* ignore */ }
    if (!slug) slug = objName || slugify(group.name) || 'site'
    return siteAddress(userId, project, slug)
  }

  if (sites.length === 0) return null

  if (sites.length === 1) {
    return (
      <button
        onClick={() => window.open(siteUrl(sites[0]), '_blank', 'noopener')}
        title={`Open website: ${sites[0].name}`}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
          padding: iconOnly ? 0 : '0 0.75rem', width: iconOnly ? 36 : undefined,
          height: 36, borderRadius: 'var(--radius-md)',
          color: 'var(--color-text)', background: 'var(--color-surface)',
          border: '1px solid var(--color-border)', fontSize: '0.875rem',
          fontWeight: 600, cursor: 'pointer',
        }}
      >
        <Globe size={14} />
        {!iconOnly && 'Project Website'}
      </button>
    )
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        title={iconOnly ? 'Open websites' : undefined}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
          padding: iconOnly ? 0 : '0 0.75rem', width: iconOnly ? 36 : undefined,
          height: 36, borderRadius: 'var(--radius-md)',
          color: 'var(--color-text)', background: 'var(--color-surface)',
          border: '1px solid var(--color-border)', fontSize: '0.875rem',
          fontWeight: 600, cursor: 'pointer',
        }}
      >
        <Globe size={14} />
        {!iconOnly && 'Project Websites'}
        <ChevronDown size={12} style={{ opacity: 0.6 }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4, minWidth: 180,
          background: 'var(--color-surface)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', zIndex: 200, overflow: 'hidden',
        }}>
          {sites.map((g, i) => (
            <button
              key={i}
              onClick={() => { setOpen(false); window.open(siteUrl(g), '_blank', 'noopener') }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
                padding: '0.5rem 0.875rem', background: 'none', border: 'none',
                cursor: 'pointer', fontSize: '0.9375rem', textAlign: 'left',
                color: 'var(--color-text)',
              }}
            >
              <Globe size={13} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
              {g.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
